/**
 * Navigation Coordinator
 *
 * Ensures mutual exclusivity between hamburger menu, search drawer, and cart drawer.
 * When one opens, the others automatically close.
 */

import { onDocumentLoaded } from "@theme/utilities";

class NavigationCoordinator {
  #mobileNav = null;
  #searchDrawer = null;
  #cartDrawer = null;
  #isInitialized = false;
  #navObserver = null;
  #desktopDrawerObservers = [];
  #isClosingFromCoordinator = false;

  constructor() {
    onDocumentLoaded(() => {
      this.#initialize();
    });
  }

  #initialize() {
    if (this.#isInitialized) return;

    // Cache references to the components
    this.#mobileNav = document.querySelector("navigation-component");
    this.#searchDrawer = document.querySelector("#malachyte-search-drawer");
    this.#cartDrawer = document.querySelector("cart-drawer-component");

    // Set up event listeners
    this.#setupListeners();
    this.#isInitialized = true;
  }

  #setupListeners() {
    // Listen for search drawer open
    if (this.#searchDrawer) {
      this.#searchDrawer.addEventListener("drawer:open", () => {
        this.#closeMobileNav();
        this.#closeCartDrawer();
      });
    }

    // Listen for cart drawer open
    if (this.#cartDrawer) {
      this.#cartDrawer.addEventListener("dialog:open", () => {
        this.#closeMobileNav();
        this.#closeSearchDrawer();
      });
    }

    // Watch for mobile nav state changes using MutationObserver
    const navDrawer = this.#mobileNav?.querySelector(".js-header__navigation");
    if (navDrawer) {
      this.#navObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === "attributes" && mutation.attributeName === "aria-hidden") {
            const isOpen = navDrawer.getAttribute("aria-hidden") === "false";

            // Only close others if menu opened and we're not in the middle of closing from coordinator
            if (isOpen && !this.#isClosingFromCoordinator) {
              this.#closeSearchDrawer();
              this.#closeCartDrawer();
            }
          }
        });
      });

      this.#navObserver.observe(navDrawer, {
        attributes: true,
        attributeFilter: ["aria-hidden"],
      });
    }

    // Watch for desktop drawer state changes
    const desktopDrawers = document.querySelectorAll(".sidebarMenu__drawerWrapper");
    desktopDrawers.forEach(drawer => {
      let wasOpen = drawer.classList.contains("is--active");

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === "attributes" && mutation.attributeName === "class") {
            const isOpen = drawer.classList.contains("is--active");

            // Only react if the state actually changed from closed to open
            if (isOpen && !wasOpen && !this.#isClosingFromCoordinator) {
              this.#closeSearchDrawer();
              this.#closeCartDrawer();
            }

            wasOpen = isOpen;
          }
        });
      });

      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ["class"],
      });

      this.#desktopDrawerObservers.push(observer);
    });
  }

  #closeMobileNav() {
    if (!this.#mobileNav) return;

    const navDrawer = this.#mobileNav.querySelector(".js-header__navigation");
    const navToggle = this.#mobileNav.querySelector(".js-nav__toggle");
    const hamburger = navToggle?.querySelector(".icon--menu");
    const close = navToggle?.querySelector(".icon--menuClose");
    const overlay = this.#mobileNav.querySelector(".sidebarMenu__drawerOverlay");

    // Check if nav is open
    if (navDrawer?.getAttribute("aria-hidden") === "false") {
      // Set flag to prevent infinite loops
      this.#isClosingFromCoordinator = true;

      // Close the mobile nav
      if (hamburger) hamburger.style.display = "block";
      if (close) close.style.display = "none";
      if (navToggle) {
        navToggle.setAttribute("aria-expanded", "false");
        navToggle.setAttribute("aria-label", "Open menu");
      }

      // Blur any focused elements inside the drawer before hiding
      const focusedElement = navDrawer.querySelector(":focus");
      if (focusedElement && focusedElement instanceof HTMLElement) {
        focusedElement.blur();
      }

      navDrawer.setAttribute("aria-hidden", "true");
      navDrawer.setAttribute("inert", "");
      document.body.classList.remove("drawer-open", "menu--open");

      if (overlay) {
        overlay.classList.remove("is--active");
      }

      // Reset flag after a short delay
      setTimeout(() => {
        this.#isClosingFromCoordinator = false;
      }, 100);
    }
  }

  #closeSearchDrawer() {
    if (!this.#searchDrawer) return;

    const drawer = this.#searchDrawer.refs?.drawer;
    if (drawer?.hasAttribute("open")) {
      this.#searchDrawer.closeDrawer();
    }
  }

  #closeCartDrawer() {
    if (!this.#cartDrawer) return;

    const dialog = this.#cartDrawer.refs?.dialog;
    if (dialog?.open) {
      this.#cartDrawer.closeDialog();
    }
  }
}

// Initialize the coordinator
new NavigationCoordinator();
